import { Resume } from './resume';
import { Return } from './return';

/**
 * Where a person who registers goes once the provider's message proves their address.
 *
 * A project that requires confirmation issues no session at registration: the person leaves this
 * screen, opens a message and comes back through the provider's link to `/confirm`. Whatever the
 * sign-in address said about where they were going would be lost on the way unless the link carries
 * it, so the link is written from the sign-in address, keeping only what that address would itself
 * follow: a `to` that `Resume` accepts (a path of this interface, or the service's own hand-off) and
 * a product's request to be returned (`?product=&return=`), which the service validates again on
 * arrival exactly as after any sign-in. Anything else is left out, so the message can never carry a
 * destination the sign-in would have ignored.
 */
export class Confirmation {
	static PATH = '/confirm';

	#parameters: URLSearchParams;
	#authority: string;

	/**
	 * @param parameters The sign-in address's query when registering, or the confirmation link's on
	 * arrival: both are read through the same filter
	 */
	constructor(parameters: URLSearchParams, authority: string) {
		this.#parameters = parameters;
		this.#authority = authority;
	}

	/** The destination that may travel with the message, and nothing else */
	get carried(): URLSearchParams {
		const kept = new URLSearchParams();
		const to = this.#parameters.get('to');
		if (to && (Resume.path(to) || Resume.handoff(to, this.#authority))) kept.set('to', to);
		if (Return.requested(this.#parameters)) {
			kept.set(Return.PRODUCT, this.#parameters.get(Return.PRODUCT) as string);
			kept.set(Return.ADDRESS, this.#parameters.get(Return.ADDRESS) as string);
		}
		return kept;
	}

	/** The address the provider's message links back to, on this interface's `origin` */
	address(origin: string): string {
		return `${origin}${Confirmation.PATH}${Confirmation.#query(this.carried)}`;
	}

	/**
	 * The hand-off the person was interrupted on, as an address of the service, once they are signed
	 * in. The product's challenge is still unspent: nobody was signed in to spend it.
	 */
	get handoff(): string | undefined {
		return Resume.handoff(this.carried.get('to'), this.#authority);
	}

	/**
	 * The sign-in address with the same destination: where a person signed in by the link goes on
	 * (the sign-in address, once signed in, is where every destination is followed), and where a link
	 * that no longer works starts again.
	 */
	get signin(): string {
		return `/signin${Confirmation.#query(this.carried)}`;
	}

	static #query(parameters: URLSearchParams): string {
		const query = parameters.toString();
		return query ? `?${query}` : '';
	}
}
